import React, { useState, useEffect, useCallback, useRef } from 'react';
import { collection, getDocs, getDoc, query, where, writeBatch, doc } from 'firebase/firestore';
import { db } from './firebase';
import useAuthStore from './store/authStore';
import { useNavigate } from 'react-router-dom';
import { SkeletonDashboard } from './components/Skeleton';
import GuiaPrimeiroAcesso from './components/GuiaPrimeiroAcesso';
import { useConfirmar } from './components/Confirmar';
import { useToast } from './components/Toast';

const cor = '#B51013';


function gerarPeriodos() {
  const lista = [];
  const hoje = new Date();
  for (let i = 0; i < 6; i++) {
    const d = new Date(hoje.getFullYear(), hoje.getMonth() - i, 1);
    lista.push({
      valor: `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`,
      label: d.toLocaleString('pt-BR', { month: 'long', year: 'numeric' }),
    });
  }
  return lista;
}

const PERIODOS = gerarPeriodos();

export default function Dashboard() {
  const usuario = useAuthStore((s) => s.usuario);
  const nivel = useAuthStore((s) => s.nivel);
  const navigate = useNavigate();

  const { confirmar, Modal: ModalConfirmar } = useConfirmar();
  const { mostrar: toast } = useToast();

  const [filtroSetor, setFiltroSetor] = useState('');
  const [filtroFuncao, setFiltroFuncao] = useState('');
  const [filtroPeriodo, setFiltroPeriodo] = useState('');
  const [setores, setSetores] = useState([]);
  const [funcoes, setFuncoes] = useState([]);
  const [dados, setDados] = useState({ ativos: 0, vencidos: 0, horas: 0, aderencia: 0, horasPorMes: {}, concluidosPorMes: {} });
  const [alertas, setAlertas] = useState([]);
  const [ranking, setRanking] = useState([]);
  const [enviandoLembretes, setEnviandoLembretes] = useState(false);
  const [carregando, setCarregando] = useState(true);
  const progressoCache = useRef([]);

  const carregarDados = useCallback(async () => {
    if (!usuario) return;
    setCarregando(true);
    try {
      // Carrega todos os colaboradores ativos de uma vez e filtra no cliente
      const colabQuery = query(collection(db, 'colaboradores'), where('status', '==', 'Ativo'));

      // Nível 1-2 só pode ler o próprio documento de progresso
      const progressoPromise = nivel >= 3
        ? getDocs(collection(db, 'progresso'))
        : getDoc(doc(db, 'progresso', usuario.uid)).then(snap => ({
            docs: snap.exists() ? [snap] : [],
            size: snap.exists() ? 1 : 0,
          }));

      const [colabSnap, progressoSnap] = await Promise.all([
        getDocs(colabQuery),
        progressoPromise,
      ]);

      // Deriva opções de setor/função do mesmo array já carregado (sem segunda leitura)
      const todosColabs = colabSnap.docs.map(d => ({ id: d.id, ...d.data() }));
      setSetores([...new Set(todosColabs.map(c => c.setor).filter(Boolean))].sort());
      setFuncoes([...new Set(todosColabs.map(c => c.funcao).filter(Boolean))].sort());

      // Aplica filtroSetor e filtroFuncao combinados via .filter() — ambos funcionam juntos
      const colaboradores = todosColabs
        .filter(c => !filtroSetor || c.setor === filtroSetor)
        .filter(c => !filtroFuncao || c.funcao === filtroFuncao);
      const ativos = colaboradores.length;

      let totalTreinamentos = 0;
      let concluidos = 0;
      let totalHoras = 0;
      let totalVencidos = 0;
      const rankingMap = {};
      const alertasLista = [];
      const horasPorMes = {};
      const concluidosPorMes = {};

      colaboradores.forEach(c => { totalTreinamentos += (c.treinamentos || []).length; });

      progressoSnap.docs.forEach(pd => {
        const p = pd.data();
        const uid = pd.id;
        const trein = p.treinamentos || {};

        const pertenceAoFiltro = filtroSetor || filtroFuncao
          ? colaboradores.some(c => c.uid === uid || c.id === uid)
          : true;
        if (!pertenceAoFiltro) return;

        if (!rankingMap[uid]) rankingMap[uid] = { uid, nome: p.nome || uid, pts: 0, horas: 0, concluidos: 0 };

        Object.entries(trein).forEach(([nome, info]) => {
          if (info.status === 'concluido') {
            const mesConc = info.concluidoEm ? info.concluidoEm.slice(0, 7) : '';
            if (filtroPeriodo && mesConc !== filtroPeriodo) return;
            concluidos++;
            const h = info.horas || 2;
            totalHoras += h;
            rankingMap[uid].pts += info.pts || 100;
            rankingMap[uid].horas += h;
            rankingMap[uid].concluidos++;
            if (mesConc) {
              horasPorMes[mesConc] = (horasPorMes[mesConc] || 0) + h;
              concluidosPorMes[mesConc] = (concluidosPorMes[mesConc] || 0) + 1;
            }
          }
          if (info.status === 'vencido') {
            totalVencidos++;
            alertasLista.push({ colaborador: p.nome || uid, treinamento: nome, uid });
          }
        });
      });

      progressoCache.current = progressoSnap.docs;
      const aderencia = totalTreinamentos > 0 ? Math.round((concluidos / totalTreinamentos) * 100) : 0;
      const rankingOrdenado = Object.values(rankingMap).sort((a, b) => b.pts - a.pts).slice(0, 5);

      setDados({ ativos, vencidos: totalVencidos, horas: totalHoras, aderencia, horasPorMes, concluidosPorMes, totalConc: concluidos, totalPend: totalTreinamentos - concluidos - totalVencidos });

      setAlertas(alertasLista.slice(0, 6));
      setRanking(rankingOrdenado);
    } catch (e) {
      console.error('Dashboard:', e);
    }
    setCarregando(false);
  }, [usuario, nivel, filtroSetor, filtroFuncao, filtroPeriodo]);

  useEffect(() => { carregarDados(); }, [carregarDados]);

  const limparFiltros = () => { setFiltroSetor(''); setFiltroFuncao(''); setFiltroPeriodo(''); };

  const enviarLembretesEmMassa = async () => {
    const ok = await confirmar({
      titulo: 'Enviar lembretes',
      mensagem: 'Enviar notificação in-app para todos os colaboradores com treinamentos vencidos ou a vencer em 30 dias?',
      confirmar: 'Enviar',
      cancelar: 'Cancelar',
    });
    if (!ok) return;
    setEnviandoLembretes(true);
    try {
      if (progressoCache.current.length === 0) {
        toast('Recarregue a página antes de enviar lembretes.', 'erro');
        setEnviandoLembretes(false);
        return;
      }
      const hoje = new Date();
      let enviados = 0;
      const batch = writeBatch(db);
      for (const pd of progressoCache.current) {
        const p = pd.data();
        const trein = p.treinamentos || {};
        const alertasFiltrados = Object.entries(trein).filter(([, info]) => {
          if (info.status === 'vencido') return true;
          if (info.status === 'concluido' && info.venceEm) {
            const dias = Math.ceil((new Date(info.venceEm) - hoje) / (1000 * 60 * 60 * 24));
            return dias <= 30 && dias >= 0;
          }
          return false;
        });
        if (alertasFiltrados.length === 0) continue;
        const ref = doc(collection(db, 'notificacoes'));
        batch.set(ref, {
          destinatarioUid: pd.id, tipo: 'lembrete',
          mensagem: `⚠️ Você tem ${alertasFiltrados.length} treinamento(s) que requer(em) atenção. Acesse Meus Treinamentos.`,
          lida: false, criadoEm: new Date().toISOString(),
        });
        enviados++;
      }
      await batch.commit();
      toast(`Lembretes enviados para ${enviados} colaborador(es).`, 'sucesso');
    } catch (e) { toast(`Erro: ${e.message}`, 'erro'); }
    setEnviandoLembretes(false);
  };
  const temFiltro = filtroSetor || filtroFuncao || filtroPeriodo;

  if (carregando) return <SkeletonDashboard />;

  // Tendências (devem ser calculadas ANTES de metricas)
  const mesAtual = new Date().toISOString().slice(0, 7);
  const mesAnterior = (() => { const d = new Date(); d.setMonth(d.getMonth() - 1); return d.toISOString().slice(0, 7); })();
  const horasAtual = dados.horasPorMes?.[mesAtual] || 0;
  const horasAnterior = dados.horasPorMes?.[mesAnterior] || 0;
  const tendHoras = horasAnterior > 0 ? Math.round(((horasAtual - horasAnterior) / horasAnterior) * 100) : null;

  const metricas = [
    { label: 'Colaboradores ativos', valor: String(dados.ativos) },
    { label: 'Aderência geral', valor: `${dados.aderencia}%`, destaque: dados.aderencia < 70 },
    { label: 'Treinamentos vencidos', valor: String(dados.vencidos), badge: dados.vencidos > 0 },
    { label: 'Horas treinadas', valor: `${dados.horas}h`, tend: tendHoras },
  ];

  const riscoAlto = dados.vencidos > 0 || dados.aderencia < 70;
  const statusGeral = dados.aderencia >= 80 ? { label: 'Sistema em dia', cor: '#1D9E75', bg: '#E1F5EE', icon: '✅' }
    : dados.aderencia >= 60 ? { label: 'Atenção necessária', cor: '#EF9F27', bg: '#FAEEDA', icon: '⚠️' }
    : { label: 'Situação crítica', cor: '#B51013', bg: '#FCEBEB', icon: '🚨' };

  return (
    <div>
      {ModalConfirmar}
      <GuiaPrimeiroAcesso />
      {/* Resumo executivo — seção colapsável */}
      {!temFiltro && dados.ativos > 0 && (
        <ResumoExecutivo dados={dados} navigate={navigate} />
      )}

      {/* Barra de status executivo */}
      {!temFiltro && dados.ativos > 0 && (
        <div style={{ background: statusGeral.bg, borderRadius: '10px', padding: '10px 16px', marginBottom: '14px', display: 'flex', alignItems: 'center', gap: '12px', border: `1px solid ${statusGeral.cor}30` }}>
          <span style={{ fontSize: '18px' }}>{statusGeral.icon}</span>
          <div style={{ flex: 1 }}>
            <span style={{ fontSize: '13px', fontWeight: '700', color: statusGeral.cor }}>{statusGeral.label}</span>
            <span style={{ fontSize: '12px', color: statusGeral.cor, opacity: 0.8, marginLeft: '8px' }}>
              {dados.aderencia}% de aderência · {dados.ativos} colaborador(es) ativo(s)
              {dados.vencidos > 0 && ` · ${dados.vencidos} treinamento(s) vencido(s)`}
            </span>
          </div>
          {riscoAlto && (
            <span onClick={() => navigate('/agenda')} style={{ fontSize: '11px', color: statusGeral.cor, cursor: 'pointer', fontWeight: '600', flexShrink: 0, textDecoration: 'underline' }}>
              Ver agenda →
            </span>
          )}
        </div>
      )}

      {/* Filtros */}
      <div style={{ background: '#fff', borderRadius: '10px', border: '1px solid #e5e5e5', padding: '12px 16px', marginBottom: '16px', display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap' }}>
        <span style={{ fontSize: '11px', fontWeight: '600', color: '#888', flexShrink: 0 }}>Filtrar:</span>

        {[
          { valor: filtroSetor, setter: setFiltroSetor, opcoes: setores, placeholder: 'Todos os setores' },
          { valor: filtroFuncao, setter: setFiltroFuncao, opcoes: funcoes, placeholder: 'Todas as funções' },
        ].map((f, i) => (
          <select key={i} value={f.valor} onChange={e => f.setter(e.target.value)} style={{
            background: f.valor ? '#FCEBEB' : '#f5f5f5', border: `1px solid ${f.valor ? cor : '#e0e0e0'}`,
            borderRadius: '8px', padding: '6px 10px', fontSize: '12px', outline: 'none',
            color: f.valor ? cor : '#555', fontWeight: f.valor ? '600' : '400', cursor: 'pointer',
          }}>
            <option value="">{f.placeholder}</option>
            {f.opcoes.map(o => <option key={o} value={o}>{o}</option>)}
          </select>
        ))}

        <select value={filtroPeriodo} onChange={e => setFiltroPeriodo(e.target.value)} style={{
          background: filtroPeriodo ? '#FCEBEB' : '#f5f5f5', border: `1px solid ${filtroPeriodo ? cor : '#e0e0e0'}`,
          borderRadius: '8px', padding: '6px 10px', fontSize: '12px', outline: 'none',
          color: filtroPeriodo ? cor : '#555', fontWeight: filtroPeriodo ? '600' : '400', cursor: 'pointer',
        }}>
          <option value="">Todos os períodos</option>
          {PERIODOS.map(p => <option key={p.valor} value={p.valor}>{p.label}</option>)}
        </select>

        {temFiltro && (
          <button onClick={limparFiltros} style={{ padding: '5px 12px', background: 'none', border: '1px solid #e0e0e0', borderRadius: '8px', fontSize: '11px', color: '#888', cursor: 'pointer' }}>
            Limpar filtros ✕
          </button>
        )}

        <div style={{ marginLeft: 'auto', fontSize: '11px', color: '#aaa' }}>
          {temFiltro && !carregando && `${dados.ativos} colaborador(es) no filtro`}
        </div>
      </div>

      {/* Métricas */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0, 1fr))', gap: '12px', marginBottom: '20px' }}>
        {metricas.map((m, i) => (
          <div key={i} style={{ background: '#fff', borderRadius: '10px', padding: '14px 16px', border: `1px solid ${m.destaque ? '#FCEBEB' : '#e5e5e5'}` }}>
            <div style={{ fontSize: '11px', color: '#888', marginBottom: '6px' }}>{m.label}</div>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: '8px' }}>
              <div style={{ fontSize: '24px', fontWeight: '600', color: m.destaque ? cor : '#1a1a1a' }}>{m.valor}</div>
              {m.tend !== null && m.tend !== undefined && (
                <span style={{ fontSize: '11px', fontWeight: '600', color: m.tend > 0 ? '#1D9E75' : m.tend < 0 ? cor : '#888' }}>
                  {m.tend > 0 ? `↑${m.tend}%` : m.tend < 0 ? `↓${Math.abs(m.tend)}%` : '→'} vs mês ant.
                </span>
              )}
            </div>
            {m.badge && (
              <span style={{ fontSize: '10px', padding: '2px 6px', borderRadius: '8px', background: '#FCEBEB', color: cor, marginTop: '4px', display: 'inline-block' }}>requer ação</span>
            )}
          </div>
        ))}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px' }}>
        {/* Alertas */}
        <div style={{ background: '#fff', borderRadius: '10px', padding: '16px', border: '1px solid #e5e5e5' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
            <div style={{ fontSize: '13px', fontWeight: '600', color: '#1a1a1a' }}>Alertas — treinamentos</div>
            {alertas.length > 0 && (
              <div style={{ display: 'flex', gap: '8px' }}>
                <span onClick={() => navigate('/agenda')} style={{ fontSize: '11px', color: cor, cursor: 'pointer', fontWeight: '600' }}>Ver agenda →</span>
                <button onClick={enviarLembretesEmMassa} disabled={enviandoLembretes} style={{ fontSize: '11px', padding: '3px 10px', background: enviandoLembretes ? '#ccc' : '#FAEEDA', border: `1px solid #EF9F27`, color: '#633806', borderRadius: '6px', cursor: 'pointer', fontWeight: '600' }}>
                  {enviandoLembretes ? '⏳' : '🔔 Lembrar todos'}
                </button>
              </div>
            )}
          </div>
          {alertas.length === 0 ? (
            <div style={{ fontSize: '13px', color: '#aaa', textAlign: 'center', padding: '20px 0' }}>Nenhum alerta{temFiltro ? ' no filtro' : ' no momento'}</div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
              {alertas.map((a, i) => (
                <div key={i} onClick={() => navigate('/colaboradores')} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '8px 10px', borderRadius: '8px', background: '#FCEBEB', cursor: 'pointer' }}>
                  <div style={{ width: '6px', height: '6px', borderRadius: '50%', background: cor, flexShrink: 0 }} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: '11px', fontWeight: '600', color: '#1a1a1a', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{a.colaborador}</div>
                    <div style={{ fontSize: '10px', color: '#888', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{a.treinamento}</div>
                  </div>
                  <span style={{ fontSize: '10px', color: cor, fontWeight: '600', flexShrink: 0 }}>Vencido</span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Ranking */}
        <div style={{ background: '#fff', borderRadius: '10px', padding: '16px', border: '1px solid #e5e5e5' }}>
          <div style={{ fontSize: '13px', fontWeight: '600', color: '#1a1a1a', marginBottom: '12px' }}>Ranking de qualificação</div>
          {ranking.length === 0 ? (
            <div style={{ fontSize: '13px', color: '#aaa', textAlign: 'center', padding: '20px 0' }}>Nenhum dado{temFiltro ? ' no filtro' : ' ainda'}</div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
              {ranking.map((r, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '6px 0', borderBottom: i < ranking.length - 1 ? '1px solid #f5f5f5' : 'none' }}>
                  <span style={{ width: '22px', height: '22px', borderRadius: '50%', background: i === 0 ? '#EF9F27' : i === 1 ? '#aaa' : i === 2 ? '#c0824a' : '#f0f0f0', color: i < 3 ? '#fff' : '#888', fontSize: '10px', fontWeight: '700', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>{i + 1}</span>
                  <span style={{ flex: 1, fontSize: '12px', color: '#1a1a1a', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.nome}</span>
                  <div style={{ textAlign: 'right', flexShrink: 0 }}>
                    <div style={{ fontSize: '12px', fontWeight: '600', color: cor }}>{r.pts} pts</div>
                    <div style={{ fontSize: '10px', color: '#888' }}>{r.horas}h</div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Gráfico de conformidade */}
      {(dados.totalConc || 0) + (dados.totalPend || 0) + dados.vencidos > 0 && (
        <div style={{ background: '#fff', borderRadius: '10px', padding: '16px', border: '1px solid #e5e5e5', marginTop: '14px' }}>
          <div style={{ fontSize: '13px', fontWeight: '600', color: '#1a1a1a', marginBottom: '14px' }}>Situação dos treinamentos</div>
          <GraficoDonut concluidos={dados.totalConc || 0} pendentes={dados.totalPend || 0} vencidos={dados.vencidos} />
        </div>
      )}

      {/* Gráficos por período */}
      {(Object.keys(dados.horasPorMes || {}).length > 0 || Object.keys(dados.concluidosPorMes || {}).length > 0) && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px', marginTop: '14px' }}>
          {Object.keys(dados.horasPorMes || {}).length > 0 && (
            <div style={{ background: '#fff', borderRadius: '10px', padding: '16px', border: '1px solid #e5e5e5' }}>
              <div style={{ fontSize: '13px', fontWeight: '600', color: '#1a1a1a', marginBottom: '14px' }}>Horas treinadas</div>
              <GraficoBarras dados={dados.horasPorMes} unidade="h" cor={cor} />
            </div>
          )}
          {Object.keys(dados.concluidosPorMes || {}).length > 0 && (
            <div style={{ background: '#fff', borderRadius: '10px', padding: '16px', border: '1px solid #e5e5e5' }}>
              <div style={{ fontSize: '13px', fontWeight: '600', color: '#1a1a1a', marginBottom: '14px' }}>Treinamentos concluídos</div>
              <GraficoBarras dados={dados.concluidosPorMes} unidade="" cor="#1D9E75" />
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function ResumoExecutivo({ dados, navigate }) {
  const [aberto, setAberto] = useState(false);

  const metaAderencia = 80;
  const aderenciaStatus = dados.aderencia >= metaAderencia ? 'ok' : dados.aderencia >= 60 ? 'atencao' : 'critico';
  const cores = { ok: '#1D9E75', atencao: '#EF9F27', critico: '#B51013' };
  const cor = cores[aderenciaStatus];

  const riscos = [
    dados.vencidos > 0 && { nivel: 'alto', texto: `${dados.vencidos} treinamento(s) vencido(s) requer(em) ação imediata`, acao: '/agenda' },
    dados.aderencia < 60 && { nivel: 'alto', texto: `Aderência crítica: ${dados.aderencia}% (meta: ${metaAderencia}%)`, acao: '/relatorios' },
    dados.aderencia >= 60 && dados.aderencia < metaAderencia && { nivel: 'medio', texto: `Aderência abaixo da meta: ${dados.aderencia}% / ${metaAderencia}%`, acao: '/relatorios' },
  ].filter(Boolean);

  return (
    <div style={{ background: '#fff', borderRadius: '10px', border: '1px solid #e5e5e5', marginBottom: '14px', overflow: 'hidden' }}>
      <div onClick={() => setAberto(v => !v)} style={{ padding: '12px 16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <div style={{ width: '10px', height: '10px', borderRadius: '50%', background: cor }} />
          <span style={{ fontSize: '13px', fontWeight: '600', color: '#1a1a1a' }}>Resumo executivo</span>
          <span style={{ fontSize: '11px', padding: '2px 8px', borderRadius: '6px', background: `${cor}15`, color: cor, fontWeight: '600' }}>
            {aderenciaStatus === 'ok' ? '✅ Meta atingida' : aderenciaStatus === 'atencao' ? '⚠️ Atenção' : '🚨 Crítico'}
          </span>
        </div>
        <span style={{ color: '#aaa', fontSize: '12px' }}>{aberto ? '▲' : '▼'}</span>
      </div>

      {aberto && (
        <div style={{ padding: '0 16px 16px', borderTop: '1px solid #f0f0f0' }}>
          {/* Riscos */}
          {riscos.length > 0 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
              <div style={{ fontSize: '11px', fontWeight: '600', color: '#888', marginBottom: '2px' }}>RISCOS IDENTIFICADOS</div>
              {riscos.map((r, i) => (
                <div key={i} onClick={() => navigate(r.acao)} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 10px', background: r.nivel === 'alto' ? '#FCEBEB' : '#FAEEDA', borderRadius: '7px', cursor: 'pointer' }}>
                  <span style={{ fontSize: '13px' }}>{r.nivel === 'alto' ? '🚨' : '⚠️'}</span>
                  <span style={{ fontSize: '11px', color: r.nivel === 'alto' ? '#791F1F' : '#633806', flex: 1 }}>{r.texto}</span>
                  <span style={{ fontSize: '11px', color: r.nivel === 'alto' ? '#B51013' : '#EF9F27', fontWeight: '600' }}>Ver →</span>
                </div>
              ))}
            </div>
          )}

          {riscos.length === 0 && (
            <div style={{ padding: '10px', background: '#E1F5EE', borderRadius: '8px', fontSize: '12px', color: '#085041', fontWeight: '500', textAlign: 'center' }}>
              ✅ Nenhum risco identificado — sistema em conformidade
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function GraficoDonut({ concluidos, pendentes, vencidos }) {
  const total = concluidos + pendentes + vencidos;
  if (total === 0) return null;

  const pctConc = concluidos / total;
  const pctPend = pendentes / total;
  const pctVenc = vencidos / total;

  const raio = 40, cx = 50, cy = 50;
  const circunferencia = 2 * Math.PI * raio;

  const fatias = [
    { pct: pctConc, cor: '#1D9E75', label: 'Concluídos', val: concluidos },
    { pct: pctPend, cor: '#EF9F27', label: 'Pendentes', val: pendentes },
    { pct: pctVenc, cor: '#B51013', label: 'Vencidos', val: vencidos },
  ];

  let offset = 0;
  const arcos = fatias.map(f => {
    const dashArray = f.pct * circunferencia;
    const dashOffset = -offset * circunferencia;
    offset += f.pct;
    return { ...f, dashArray, dashOffset };
  });

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap' }}>
      <svg viewBox="0 0 100 100" style={{ width: '100px', height: '100px', flexShrink: 0 }}>
        <circle cx={cx} cy={cy} r={raio} fill="none" stroke="#f0f0f0" strokeWidth="12" />
        {arcos.map((a, i) => (
          <circle key={i} cx={cx} cy={cy} r={raio} fill="none"
            stroke={a.cor} strokeWidth="12"
            strokeDasharray={`${a.dashArray} ${circunferencia - a.dashArray}`}
            strokeDashoffset={a.dashOffset}
            transform={`rotate(-90 ${cx} ${cy})`}
          />
        ))}
        <text x={cx} y={cy - 4} textAnchor="middle" fontSize="14" fontWeight="800" fill="#1a1a1a">{total}</text>
        <text x={cx} y={cy + 10} textAnchor="middle" fontSize="8" fill="#aaa">treinos</text>
      </svg>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
        {fatias.map((f, i) => (
          <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <div style={{ width: '10px', height: '10px', borderRadius: '50%', background: f.cor, flexShrink: 0 }} />
            <span style={{ fontSize: '11px', color: '#888', flex: 1 }}>{f.label}</span>
            <span style={{ fontSize: '12px', fontWeight: '700', color: f.cor }}>{f.val}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function GraficoBarras({ dados, unidade = 'h', cor: corBarra = '#B51013' }) {
  const entradas = Object.entries(dados).sort(([a], [b]) => a.localeCompare(b)).slice(-6);
  if (entradas.length === 0) return null;

  const maxVal = Math.max(...entradas.map(([, v]) => v), 1);
  const altura = 80;
  const largBarra = 36;
  const gap = 12;
  const largTotal = entradas.length * (largBarra + gap) - gap;

  return (
    <div style={{ overflowX: 'auto' }}>
      <svg width={largTotal} height={altura + 32} style={{ display: 'block', minWidth: '100%' }}>
        {entradas.map(([mes, horas], i) => {
          const x = i * (largBarra + gap);
          const h = Math.round((horas / maxVal) * altura);
          const isAtual = i === entradas.length - 1;
          const label = mes.slice(5) + '/' + mes.slice(2, 4);

          return (
            <g key={mes}>
              {/* Barra */}
              <rect
                x={x} y={altura - h}
                width={largBarra} height={h}
                rx="4"
                fill={isAtual ? corBarra : '#f0f0f0'}
              />
              {/* Valor acima */}
              <text
                x={x + largBarra / 2} y={altura - h - 5}
                textAnchor="middle"
                fontSize="10"
                fill={isAtual ? corBarra : '#888'}
                fontWeight={isAtual ? '700' : '400'}
              >
                {horas}{unidade}
              </text>
              {/* Label abaixo */}
              <text
                x={x + largBarra / 2} y={altura + 16}
                textAnchor="middle"
                fontSize="10"
                fill={isAtual ? corBarra : '#aaa'}
                fontWeight={isAtual ? '600' : '400'}
              >
                {label}
              </text>
            </g>
          );
        })}
      </svg>
    </div>
  );
}
